import clsx from 'clsx';
import React from 'react';

import userImg from '../../../../assets/user.png';
import greenTick from '../../../../assets/green-tick.svg';
import { alertIcon, ctaPurpleRight } from '../../../../assets/hostedassets';
import { isEmpty, toTitleCase } from '../../../../utils/utils';
import { DATE_FORMATS, formatDateByFormat, formatTenureYears } from '../../../../utils/dateUtils';
import { KEYBOARD_EVENTS, TabModule } from '../../../../utils/constants/commonConstants';
import { BureauType } from '../CreditBureau/components/Report/CreditBureauConstants';
import { PillCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/PillCellRenderer/PillCellRenderer';
import { CellRendererProps } from '../../../../common/_custom/DataTable/DataTable.types';
// MANAGEMENT_PROFILE_COMPONENT_FIELDS: dispatched on by the COMPONENT renderer registry below.
// The polus seed sets no `componentName` on any of these columns (D6), so dispatch is on
// `column.field` — a `componentName` switch would silently render blank (pattern:
// GstinTable.tsx:263-278).
import { ColumnLockKey, MANAGEMENT_PROFILE_COMPONENT_FIELDS } from './ManagementProfileTabConstants';
import { DirectorTableRow } from './useManagementProfileTabLogic';
import styles from './ManagementProfileTab.module.scss';

function NameCell({ row }: { row: DirectorTableRow }) {
    return (
        <div className={styles.nameCell}>
            <div className={styles.avatar}>
                <img
                    src={row.imageUrl || userImg}
                    alt={row.name}
                    onError={({ currentTarget }) => {
                        currentTarget.onerror = null;
                        currentTarget.src = userImg;
                    }}
                />
            </div>
            <div className={styles.nameInfo}>
                <span className={styles.directorName}>{toTitleCase(row.name)}</span>
                {row.linkedinUrl && (
                    <a
                        href={row.linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.linkedInLink}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <span className={styles.followersCount}>{row.followersCount || 'LinkedIn'}</span>
                    </a>
                )}
            </div>
        </div>
    );
}

function TenureCell({ row }: { row: DirectorTableRow }) {
    if (!row.tenureBeginDate) return <span className={styles.cellDash}>-</span>;
    return (
        <div className={styles.tenureInfo}>
            <span className={styles.tenureYears}>{formatTenureYears(row.tenureBeginDate, row.tenureEndDate)}</span>
            <span className={styles.tenureDates}>
                ({formatDateByFormat(row.tenureBeginDate, DATE_FORMATS.MONTH_SHORT_YEAR)} -{' '}
                {row.tenureEndDate ? formatDateByFormat(row.tenureEndDate, DATE_FORMATS.MONTH_SHORT_YEAR) : row.isActive ? 'Present' : ''})
            </span>
        </div>
    );
}

function ContactCell({ row }: { row: DirectorTableRow }) {
    if (row.loading) return <span className={styles.cellDash}>-</span>;
    if (!row.email && !row.phone) return <span className={styles.cellDash}>-</span>;
    return (
        <div className={styles.contactInfo}>
            {row.email && <span className={styles.contactEmail}>{row.email}</span>}
            {row.phone && <span className={styles.contactPhone}>{row.phone}</span>}
        </div>
    );
}

// Enter/Space activation for the click-through cells below — they're plain `div`s (not
// `<button>`), so keyboard/screen-reader users need an explicit role + key handler.
function handleActivationKeyDown(e: React.KeyboardEvent<HTMLDivElement>, onNavigate: () => void) {
    if (e.key === KEYBOARD_EVENTS.ENTER || e.key === KEYBOARD_EVENTS.SPACE) {
        e.preventDefault();
        e.stopPropagation();
        onNavigate();
    }
}

interface AlertCountCellProps {
    row: DirectorTableRow;
    locked: boolean;
    count?: number | null;
    singularLabel: string;
    pluralLabel: string;
    emptyLabel: string;
    onNavigate: () => void;
}

// Shared by riskIndicatorCount and amlPepRiskCount — both share the "red count / grey
// no-alerts / dash" three-state shape, differing only in copy and the click-through target.
function AlertCountCell({ row, locked, count, singularLabel, pluralLabel, emptyLabel, onNavigate }: AlertCountCellProps) {
    const clickable = !locked && !row.loading && !!count;
    return (
        <div
            className={clsx({ [styles.alertClickable]: clickable })}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={
                clickable
                    ? (e) => {
                          e.stopPropagation();
                          onNavigate();
                      }
                    : undefined
            }
            onKeyDown={clickable ? (e) => handleActivationKeyDown(e, onNavigate) : undefined}
        >
            {locked || row.loading || count == null ? (
                <span className={styles.cellDash}>-</span>
            ) : count > 0 ? (
                <span className={styles.alertRed}>
                    <img src={alertIcon} alt="" height="14px" />
                    {`${count} ${count === 1 ? singularLabel : pluralLabel}`}
                </span>
            ) : (
                <span className={styles.alertGrey}>
                    <img src={greenTick} alt="" height="14px" />
                    {emptyLabel}
                </span>
            )}
        </div>
    );
}

// legalCasesCount has no "No Cases" grey state — a zero count shows the dash, same as an
// unknown count. Kept separate from AlertCountCell rather than adding another flag for it.
function LegalCasesCell({ row, locked, onNavigate }: { row: DirectorTableRow; locked: boolean; onNavigate: () => void }) {
    const count = row.legalCasesCount;
    const clickable = !locked && !row.loading && !!count;
    return (
        <div
            className={clsx({ [styles.alertClickable]: clickable })}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={
                clickable
                    ? (e) => {
                          e.stopPropagation();
                          onNavigate();
                      }
                    : undefined
            }
            onKeyDown={clickable ? (e) => handleActivationKeyDown(e, onNavigate) : undefined}
        >
            {locked || row.loading || count == null || count === 0 ? (
                <span className={styles.cellDash}>-</span>
            ) : (
                <span className={styles.alertRed}>
                    <img src={alertIcon} alt="" height="14px" />
                    {`${count} ${count === 1 ? 'Case' : 'Cases'}`}
                </span>
            )}
        </div>
    );
}

function CreditScoreCell({ row, onNavigate }: { row: DirectorTableRow; onNavigate: () => void }) {
    const isClickable = !row.loading && typeof row.creditScore === 'number' && !Number.isNaN(row.creditScore);
    return (
        <div
            className={clsx({ [styles.creditClickable]: isClickable })}
            role={isClickable ? 'button' : undefined}
            tabIndex={isClickable ? 0 : undefined}
            onClick={
                isClickable
                    ? (e) => {
                          e.stopPropagation();
                          onNavigate();
                      }
                    : undefined
            }
            onKeyDown={isClickable ? (e) => handleActivationKeyDown(e, onNavigate) : undefined}
        >
            {isClickable ? (
                <span className={styles.creditScore}>
                    <img src={ctaPurpleRight} alt="" height="14px" />
                    {row.creditScore}
                </span>
            ) : (
                <span className={styles.cellDash}>-</span>
            )}
        </div>
    );
}

interface ManagementProfileComponentRegistryDeps {
    columnLocks: Record<ColumnLockKey, boolean>;
    navigateToTab?: (tabId: string, additionalParams?: Record<string, string>) => void;
}

/**
 * Builds the `componentRenderFnRegistry` handed to `ComponentCellRenderer` for every
 * `COMPONENT`-typed Management Profile column. Lock state and the navigation callback are
 * captured by closure (not via `show`) — column locks gate click-through/cell content, they
 * never hide the column (D6).
 */
export function createManagementProfileComponentRegistry({ columnLocks, navigateToTab }: ManagementProfileComponentRegistryDeps) {
    return function managementProfileComponentRenderFnRegistry({ row, column }: { row: DirectorTableRow; column?: any }) {
        switch (column?.field) {
            case MANAGEMENT_PROFILE_COMPONENT_FIELDS.NAME:
                return <NameCell row={row} />;
            case MANAGEMENT_PROFILE_COMPONENT_FIELDS.TENURE:
                return <TenureCell row={row} />;
            case MANAGEMENT_PROFILE_COMPONENT_FIELDS.CONTACT:
                return <ContactCell row={row} />;
            case MANAGEMENT_PROFILE_COMPONENT_FIELDS.RISK_INDICATOR_COUNT:
                return (
                    <AlertCountCell
                        row={row}
                        locked={columnLocks.riskAlerts}
                        count={row.riskIndicatorCount}
                        singularLabel="Alert"
                        pluralLabel="Alerts"
                        emptyLabel="No Alerts"
                        onNavigate={() =>
                            navigateToTab?.(TabModule.RELATED_PARTY, { directorName: row.name ?? '', directorDin: row.din ?? '' })
                        }
                    />
                );
            case MANAGEMENT_PROFILE_COMPONENT_FIELDS.AML_PEP_RISK_COUNT:
                return (
                    <AlertCountCell
                        row={row}
                        locked={columnLocks.regulatoryChecks}
                        count={row.amlPepRiskCount}
                        singularLabel="Indicator"
                        pluralLabel="Indicators"
                        emptyLabel="No Indicator"
                        onNavigate={() =>
                            navigateToTab?.(TabModule.LEGAL_CHECKS, {
                                directorName: row.name ?? '',
                                directorDin: row.din ?? '',
                                section: 'regulatoryChecks',
                            })
                        }
                    />
                );
            case MANAGEMENT_PROFILE_COMPONENT_FIELDS.LEGAL_CASES_COUNT:
                return (
                    <LegalCasesCell
                        row={row}
                        locked={columnLocks.legalCases}
                        onNavigate={() =>
                            navigateToTab?.(TabModule.LEGAL_CHECKS, { directorName: row.name ?? '', directorDin: row.din ?? '' })
                        }
                    />
                );
            case MANAGEMENT_PROFILE_COMPONENT_FIELDS.CREDIT_SCORE:
                return (
                    <CreditScoreCell
                        row={row}
                        onNavigate={() =>
                            navigateToTab?.(TabModule.CREDIT_BUREAU, {
                                bureauType: BureauType.ALL,
                                directorName: row.name ?? '',
                                focusDirectorId: row.directorId ?? '',
                            })
                        }
                    />
                );
            default:
                return null;
        }
    };
}

// PILL adapter — PillCellRenderer takes `{label, isActive}`, not `CellRendererProps`
// (pattern: OrgBasedUsersTable.tsx:401-405). `isActive` keeps its bespoke Active/Former
// copy (the system Status column); any other PILL column (admin-created custom field,
// G-5) falls back to a generic boolean pill so it renders something instead of going
// blank — same isEmpty→dash / truthy→pill convention used by every other ad hoc PILL
// renderer in this repo (OrgBasedUsersTable.tsx, MandateSharingDrawer.tsx).
export function ManagementProfileStatusPillRenderer({ value, column }: CellRendererProps) {
    if (column?.field === 'isActive') {
        if (value === null || value === undefined) return <span className={styles.cellDash}>-</span>;
        return <PillCellRenderer label={value ? 'Active' : 'Former'} isActive={!!value} />;
    }
    if (isEmpty(value)) return <span className={styles.cellDash}>-</span>;
    return <PillCellRenderer label={value ? 'Yes' : 'No'} isActive={!!value} />;
}
